import React from 'react'
import gql from 'graphql-tag'
import { Query } from 'react-apollo'
import { ServerEnvironment } from '../server-environment'

export const reportingInfoQuery = gql`
  query ReportingInfoQuery(
    $organizationID: ID!
    $applicationID: ID!
    $vulnerabilityStatuses: [String!]
    $vulnerabilitySeverities: [String!]
    $vulnerabilityTypes: [String!]
    $vulnerabilityTags: [String!]
    $serverEnvironments: [String!]
    $serverTags: [String!]
    $complianceReports: [String!]
    $showVulnerabilitiesDetails: Boolean!
    $showRouteObservations: Boolean!
  ) {
    reportingInfo(
      organizationID: $organizationID
      applicationID: $applicationID
      filters: {
        vulnerabilityStatuses: $vulnerabilityStatuses
        vulnerabilitySeverities: $vulnerabilitySeverities
        vulnerabilityTypes: $vulnerabilityTypes
        vulnerabilityTags: $vulnerabilityTags
        serverEnvironments: $serverEnvironments
        serverTags: $serverTags
        complianceReports: $complianceReports
        showVulnerabilitiesDetails: $showVulnerabilitiesDetails
        showRouteObservations: $showRouteObservations
      }
    ) {
      vulnTotal
      vulnerabilitiesWithDetailsLimit
      vulnerabilitiesWithoutDetailsLimit
      routesWithDetailsLimit
      routesWithoutDetailsLimit
      maxVulnsAndRoutesWithDetails
      maxVulnsAndRoutesWithoutDetails
      totalRoutes
      applicationName
    }
  }
`

export interface ReportingInfo {
  vulnTotal: number
  vulnerabilitiesWithDetailsLimit: number
  vulnerabilitiesWithoutDetailsLimit: number
  routesWithDetailsLimit: number
  routesWithoutDetailsLimit: number
  maxVulnsAndRoutesWithDetails: number
  maxVulnsAndRoutesWithoutDetails: number
  totalRoutes: number
  applicationName: string
  refetch: () => Promise<any>
}

interface ReportingInfoQueryProps {
  organizationID: string
  applicationID: string
  filters: {
    vulnerabilityStatuses: string[]
    vulnerabilitySeverities: string[]
    vulnerabilityTypes: string[]
    vulnerabilityTags: string[]
    serverEnvironments: ServerEnvironment[]
    serverTags: string[]
    complianceReports: string[]
    showVulnerabilitiesDetails: boolean
    showRouteObservations: boolean
  }
  children: (props: {
    vulnTotal: number
    vulnerabilitiesWithDetailsLimit: number
    vulnerabilitiesWithoutDetailsLimit: number
    routesWithDetailsLimit: number
    routesWithoutDetailsLimit: number
    maxVulnsAndRoutesWithDetails: number
    maxVulnsAndRoutesWithoutDetails: number
    totalRoutes: number
    applicationName: string
    loading: boolean
    refetch: () => Promise<any>
  }) => React.ReactNode
}

export function ReportingInfoQuery({
  children,
  applicationID,
  organizationID,
  filters
}: ReportingInfoQueryProps) {
  const {
    vulnerabilityStatuses,
    vulnerabilitySeverities,
    vulnerabilityTypes,
    vulnerabilityTags,
    serverEnvironments,
    serverTags,
    complianceReports,
    showVulnerabilitiesDetails,
    showRouteObservations
  } = filters
  return (
    <Query<
      {
        readonly reportingInfo: ReportingInfo
      },
      {}
    >
      variables={{
        organizationID,
        applicationID,
        vulnerabilityStatuses,
        vulnerabilitySeverities,
        vulnerabilityTypes,
        vulnerabilityTags,
        serverEnvironments,
        serverTags,
        complianceReports,
        showVulnerabilitiesDetails,
        showRouteObservations
      }}
      query={reportingInfoQuery}
      fetchPolicy="network-only"
    >
      {({ data, loading, refetch }) => {
        if (!data || !data.reportingInfo) {
          return <div />
        }

        return children({
          vulnTotal: data.reportingInfo.vulnTotal,
          vulnerabilitiesWithDetailsLimit:
            data.reportingInfo.vulnerabilitiesWithDetailsLimit,
          vulnerabilitiesWithoutDetailsLimit:
            data.reportingInfo.vulnerabilitiesWithoutDetailsLimit,
          routesWithDetailsLimit: data.reportingInfo.routesWithDetailsLimit,
          routesWithoutDetailsLimit:
            data.reportingInfo.routesWithoutDetailsLimit,
          maxVulnsAndRoutesWithDetails:
            data.reportingInfo.maxVulnsAndRoutesWithDetails,
          maxVulnsAndRoutesWithoutDetails:
            data.reportingInfo.maxVulnsAndRoutesWithoutDetails,
          totalRoutes: data.reportingInfo.totalRoutes,
          applicationName: data.reportingInfo.applicationName,
          loading,
          refetch
        })
      }}
    </Query>
  )
}
